/**
 * PostPipeline — the frame after the scene pass: SSAO, bloom, god rays and the grade, on plain three.
 *
 * ONE PATH FOR EVERY TIER. The scene renders into an SRGB8_ALPHA8 target: the hardware blends in
 * linear light and encodes on write, so translucent water composites the same on Low as on Ultra
 * (the old bypass blended in gamma space and the water read lighter there), at the canvas's own
 * 4 bytes per pixel. A preset toggles STAGES, never the path — Low is Ultra minus stages — and the
 * composite grades and encodes once at the end, so no material carries its own grade or encode.
 *
 * Fewer passes than the library it replaces: the AO's normals come from depth derivatives, so the
 * scene is drawn once (the NormalPass drew it twice); bloom is one threshold plus a shrinking mip
 * chain; the rays' occlusion mask is read from the depth texture instead of rendering a sun mesh.
 * Values above 1.0 clamp at the target, so bloom keys off clamped values: the disc, the horizon glow
 * and specular still bloom, a whole sunlit hillside no longer does.
 *
 * MSAA is `samples` on the scene target, off by default; three resolves colour and depth on the
 * blit, so the depth texture is the resolved depth.
 */

import * as THREE from 'three';
import { useGameStore } from '../../state/store';
import { getSunLight, getMoonLight, getSunDirection, getMoonDirection } from '../scene/Lighting';
import { smoothstep } from '@worldify/shared';
import {
  fullscreenVertex, ssaoFragment, aoBlurFragment, luminanceFragment, downsampleFragment,
  upsampleFragment, raysMaskFragment, raysRadialFragment, kawaseFragment, compositeFragment,
} from './postShaders.js';

interface Settings {
  msaaSamples: number;
  ssaoEnabled: boolean;
  ssaoIntensity: number;
  ssaoRadius: number;
  bloomEnabled: boolean;
  bloomIntensity: number;
  bloomThreshold: number;
  bloomRadius: number;
  godRaysEnabled: boolean;
  godRaysDecay: number;
  godRaysExposure: number;
  /** Store units: 0-2, 1 = neutral. */
  saturation: number;
}

/** The library's kernel and falloffs, kept so the AO reads the same. */
const SSAO_SAMPLES = 9;
const SSAO_RINGS = 7;
const SSAO_BIAS = 0.025;
const SSAO_FADE = 0.01;
const SSAO_MIN_RADIUS_SCALE = 0.33;
const SSAO_DISTANCE_CUTOFF = new THREE.Vector2(0.02, 0.0225);
const SSAO_PROXIMITY_CUTOFF = new THREE.Vector2(0.0003, 0.0004);
const BLOOM_SMOOTHING = 0.03;
const BLOOM_LEVELS = 8;
const RAYS_DENSITY = 0.96;
const RAYS_WEIGHT = 0.3;
const RAYS_CLAMP_MAX = 1.0;
/** The light sits at this fraction of the far plane when projected to find the ray origin. */
const RAYS_FAR_FRACTION = 0.9;
/** Horizon band (sin of elevation) the body fades over, matching the sky shader's disc gate. */
const RAYS_HORIZON_FADE = 0.1;
/** The old god-rays sun mesh: a sphere of this radius at the config's body distance. */
const RAYS_MESH_RADIUS = 30;

let godRaysSamples = 60;
let pipeline: PostPipeline | null = null;

function fullscreenTriangle(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
  return geo;
}

function pass(fragmentShader: string, uniforms: Record<string, THREE.IUniform>, defines?: Record<string, string | number>): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: fullscreenVertex, fragmentShader, uniforms, defines,
    depthTest: false, depthWrite: false, blending: THREE.NoBlending,
  });
}

function colorTarget(w: number, h: number, opts: { srgb: boolean; red?: boolean } & Partial<THREE.RenderTargetOptions>): THREE.WebGLRenderTarget {
  return new THREE.WebGLRenderTarget(w, h, {
    type: THREE.UnsignedByteType,
    format: opts.red ? THREE.RedFormat : THREE.RGBAFormat,
    colorSpace: opts.srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace,
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    generateMipmaps: false, depthBuffer: false, stencilBuffer: false,
    ...opts,
  });
}

export class PostPipeline {
  private readonly quadScene = new THREE.Scene();
  private readonly quad: THREE.Mesh;
  private readonly quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  private scene!: THREE.WebGLRenderTarget;
  private ao!: THREE.WebGLRenderTarget;
  private aoBlur!: THREE.WebGLRenderTarget;
  private bloom: THREE.WebGLRenderTarget[] = [];
  private bloomUp: THREE.WebGLRenderTarget[] = [];
  private raysA!: THREE.WebGLRenderTarget;
  private raysB!: THREE.WebGLRenderTarget;
  private width = 1;
  private height = 1;

  private readonly ssaoMat: THREE.ShaderMaterial;
  private readonly aoBlurMat: THREE.ShaderMaterial;
  private readonly luminanceMat: THREE.ShaderMaterial;
  private readonly downMat: THREE.ShaderMaterial;
  private readonly upMat: THREE.ShaderMaterial;
  private readonly raysMaskMat: THREE.ShaderMaterial;
  private readonly raysRadialMat: THREE.ShaderMaterial;
  private readonly kawaseMat: THREE.ShaderMaterial;
  private readonly compositeMat: THREE.ShaderMaterial;

  private settings: Settings;
  private readonly unsubscribe: () => void;
  private readonly invProj = new THREE.Matrix4();
  private readonly lightDir = new THREE.Vector3();
  private readonly lightPos = new THREE.Vector3();
  private readonly lightColor = new THREE.Color();

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    const s = useGameStore.getState();
    this.settings = {
      msaaSamples: s.msaaSamples,
      ssaoEnabled: s.quality.ssaoEnabled, ssaoIntensity: s.environment.ssaoIntensity, ssaoRadius: s.environment.ssaoRadius,
      bloomEnabled: s.quality.bloomEnabled, bloomIntensity: s.environment.bloomIntensity,
      bloomThreshold: s.environment.bloomThreshold, bloomRadius: s.environment.bloomRadius,
      godRaysEnabled: s.quality.godRaysEnabled, godRaysDecay: s.environment.godRaysDecay,
      godRaysExposure: s.environment.godRaysExposure,
      saturation: s.environment.saturation,
    };

    const depthUniforms = () => ({
      tDepth: { value: null }, uInvProj: { value: this.invProj }, uNear: { value: 0.1 }, uFar: { value: 1000 },
    });
    this.ssaoMat = pass(ssaoFragment, {
      ...depthUniforms(), uTexel: { value: new THREE.Vector2() }, uProj: { value: new THREE.Matrix4() },
      uRadius: { value: 0.1 }, uBias: { value: SSAO_BIAS }, uFade: { value: SSAO_FADE },
      uMinRadiusScale: { value: SSAO_MIN_RADIUS_SCALE },
      uDistanceCutoff: { value: SSAO_DISTANCE_CUTOFF }, uProximityCutoff: { value: SSAO_PROXIMITY_CUTOFF },
    }, { SAMPLES: SSAO_SAMPLES * SSAO_RINGS, RINGS: SSAO_RINGS });
    this.aoBlurMat = pass(aoBlurFragment, { ...depthUniforms(), tAO: { value: null }, uTexel: { value: new THREE.Vector2() } });
    this.luminanceMat = pass(luminanceFragment, { tInput: { value: null }, uThreshold: { value: 0.8 }, uSmoothing: { value: BLOOM_SMOOTHING } });
    this.downMat = pass(downsampleFragment, { tInput: { value: null }, uTexel: { value: new THREE.Vector2() } });
    this.upMat = pass(upsampleFragment, { tInput: { value: null }, tSupport: { value: null }, uTexel: { value: new THREE.Vector2() }, uRadius: { value: 0.5 } });
    this.raysMaskMat = pass(raysMaskFragment, {
      tDepth: { value: null }, uInvProj: { value: this.invProj }, uCamWorld: { value: new THREE.Matrix4() },
      uLightDir: { value: this.lightDir }, uLightColor: { value: this.lightColor },
      uCosEdge: { value: 0.996 }, uCosCore: { value: 0.9985 },
    });
    this.raysRadialMat = pass(raysRadialFragment, {
      tInput: { value: null }, uLightUv: { value: new THREE.Vector2() }, uDensity: { value: RAYS_DENSITY },
      uDecay: { value: 0.85 }, uWeight: { value: RAYS_WEIGHT }, uExposure: { value: 0.3 },
      uClampMax: { value: RAYS_CLAMP_MAX }, uSamples: { value: godRaysSamples },
    });
    this.kawaseMat = pass(kawaseFragment, { tInput: { value: null }, uTexel: { value: new THREE.Vector2() }, uOffset: { value: 0.5 } });
    this.compositeMat = pass(compositeFragment, {
      ...depthUniforms(), tScene: { value: null }, tAO: { value: null }, tBloom: { value: null }, tRays: { value: null },
      uAoTexel: { value: new THREE.Vector2() }, uUseAO: { value: 0 }, uUseBloom: { value: 0 }, uUseRays: { value: 0 },
      uAoIntensity: { value: 4 }, uBloomIntensity: { value: 1 }, uGrade: { value: 0 },
    });

    this.quad = new THREE.Mesh(fullscreenTriangle(), this.compositeMat);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    this.allocate(Math.max(1, size.x), Math.max(1, size.y));
    this.applySettings(this.settings);

    this.unsubscribe = useGameStore.subscribe((state, prev) => {
      const c: Partial<Settings> = {};
      if (state.msaaSamples !== prev.msaaSamples) c.msaaSamples = state.msaaSamples;
      if (state.quality.ssaoEnabled !== prev.quality.ssaoEnabled) c.ssaoEnabled = state.quality.ssaoEnabled;
      if (state.environment.ssaoIntensity !== prev.environment.ssaoIntensity) c.ssaoIntensity = state.environment.ssaoIntensity;
      if (state.environment.ssaoRadius !== prev.environment.ssaoRadius) c.ssaoRadius = state.environment.ssaoRadius;
      if (state.quality.bloomEnabled !== prev.quality.bloomEnabled) c.bloomEnabled = state.quality.bloomEnabled;
      if (state.environment.bloomIntensity !== prev.environment.bloomIntensity) c.bloomIntensity = state.environment.bloomIntensity;
      if (state.environment.bloomThreshold !== prev.environment.bloomThreshold) c.bloomThreshold = state.environment.bloomThreshold;
      if (state.environment.bloomRadius !== prev.environment.bloomRadius) c.bloomRadius = state.environment.bloomRadius;
      if (state.quality.godRaysEnabled !== prev.quality.godRaysEnabled) c.godRaysEnabled = state.quality.godRaysEnabled;
      if (state.environment.godRaysDecay !== prev.environment.godRaysDecay) c.godRaysDecay = state.environment.godRaysDecay;
      if (state.environment.godRaysExposure !== prev.environment.godRaysExposure) c.godRaysExposure = state.environment.godRaysExposure;
      if (state.environment.saturation !== prev.environment.saturation) c.saturation = state.environment.saturation;
      if (Object.keys(c).length > 0) this.applySettings(c);
    });
  }

  /** Materials the shader warm-up compiles: those drawn into a target, and the one drawn to the canvas. */
  materialsForWarmup(): { target: THREE.Material[]; canvas: THREE.Material[] } {
    return {
      target: [this.ssaoMat, this.aoBlurMat, this.luminanceMat, this.downMat, this.upMat, this.raysMaskMat, this.raysRadialMat, this.kawaseMat],
      canvas: [this.compositeMat],
    };
  }

  /** Drawing-buffer pixels. */
  setSize(width: number, height: number): void {
    if (width === this.width && height === this.height) return;
    this.disposeTargets();
    this.allocate(width, height);
  }

  render(scene: THREE.Scene, camera: THREE.PerspectiveCamera): void {
    const r = this.renderer;
    const s = this.settings;
    r.setRenderTarget(this.scene);
    r.render(scene, camera);

    this.invProj.copy(camera.projectionMatrixInverse);
    const depth = this.scene.depthTexture;
    for (const m of [this.ssaoMat, this.aoBlurMat, this.compositeMat]) {
      m.uniforms.tDepth.value = depth;
      m.uniforms.uNear.value = camera.near;
      m.uniforms.uFar.value = camera.far;
    }

    if (s.ssaoEnabled) {
      this.ssaoMat.uniforms.uProj.value.copy(camera.projectionMatrix);
      this.blit(this.ssaoMat, this.ao);
      this.aoBlurMat.uniforms.tAO.value = this.ao.texture;
      this.blit(this.aoBlurMat, this.aoBlur);
    }

    if (s.bloomEnabled && this.bloom.length > 0) {
      this.luminanceMat.uniforms.tInput.value = this.scene.texture;
      this.blit(this.luminanceMat, this.bloom[0]);
      for (let i = 1; i < this.bloom.length; i++) {
        const src = this.bloom[i - 1];
        this.downMat.uniforms.tInput.value = src.texture;
        this.downMat.uniforms.uTexel.value.set(1 / src.width, 1 / src.height);
        this.blit(this.downMat, this.bloom[i]);
      }
      let below = this.bloom[this.bloom.length - 1];
      for (let i = this.bloom.length - 2; i >= 0; i--) {
        this.upMat.uniforms.tInput.value = below.texture;
        this.upMat.uniforms.uTexel.value.set(1 / below.width, 1 / below.height);
        this.upMat.uniforms.tSupport.value = this.bloom[i].texture;
        this.blit(this.upMat, this.bloomUp[i]);
        below = this.bloomUp[i];
      }
    }

    const rays = s.godRaysEnabled && this.prepareRays(camera);
    if (rays) {
      // Mask, blur, then the march — the old effect's order (the blur softens the disc, not the rays).
      this.raysMaskMat.uniforms.tDepth.value = depth;
      this.raysMaskMat.uniforms.uCamWorld.value.copy(camera.matrixWorld);
      this.blit(this.raysMaskMat, this.raysA);
      this.kawaseMat.uniforms.uTexel.value.set(1 / this.raysA.width, 1 / this.raysA.height);
      this.kawaseMat.uniforms.tInput.value = this.raysA.texture;
      this.kawaseMat.uniforms.uOffset.value = 0.5;
      this.blit(this.kawaseMat, this.raysB);
      this.kawaseMat.uniforms.tInput.value = this.raysB.texture;
      this.kawaseMat.uniforms.uOffset.value = 1.5;
      this.blit(this.kawaseMat, this.raysA);
      this.raysRadialMat.uniforms.tInput.value = this.raysA.texture;
      this.raysRadialMat.uniforms.uSamples.value = godRaysSamples;
      this.blit(this.raysRadialMat, this.raysB);
    }

    const u = this.compositeMat.uniforms;
    u.tScene.value = this.scene.texture;
    u.tAO.value = this.aoBlur.texture;
    u.tBloom.value = this.bloomUp[0]?.texture ?? null;
    u.tRays.value = this.raysB.texture;
    u.uUseAO.value = s.ssaoEnabled ? 1 : 0;
    u.uUseBloom.value = s.bloomEnabled && this.bloomUp.length > 0 ? 1 : 0;
    u.uUseRays.value = rays ? 1 : 0;
    this.blit(this.compositeMat, null);
  }

  dispose(): void {
    this.unsubscribe();
    this.disposeTargets();
    for (const m of [this.ssaoMat, this.aoBlurMat, this.luminanceMat, this.downMat, this.upMat,
      this.raysMaskMat, this.raysRadialMat, this.kawaseMat, this.compositeMat]) m.dispose();
    this.quad.geometry.dispose();
  }

  private blit(material: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget | null): void {
    this.quad.material = material;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.quadScene, this.quadCamera);
  }

  /** Whether a body is up and in front of the camera; sets the mask and radial uniforms. */
  private prepareRays(camera: THREE.PerspectiveCamera): boolean {
    const sun = getSunLight();
    const moon = getMoonLight();
    getSunDirection(this.lightDir);
    const isMoon = moon !== null && this.lightDir.y < 0;
    if (isMoon) getMoonDirection(this.lightDir);
    const light = isMoon ? moon : sun;
    if (!light) return false;
    const fade = smoothstep(-RAYS_HORIZON_FADE, RAYS_HORIZON_FADE, this.lightDir.y);
    if (fade <= 0) return false;
    this.lightPos.copy(camera.position).addScaledVector(this.lightDir, camera.far * RAYS_FAR_FRACTION).project(camera);
    if (this.lightPos.z > 1) return false;
    this.lightColor.copy(light.color).multiplyScalar(fade);
    const cfg = useGameStore.getState().dayNightConfig;
    const size = isMoon ? cfg.moonSize : cfg.sunSize;
    const sizeRef = Math.max(1, isMoon ? cfg.moonDistance : cfg.sunDistance);
    // THE BODY IS THE OLD GOD-RAYS MESH: a sphere of radius RAYS_MESH_RADIUS at `sizeRef`, scaled by the
    // config size — a hard disc of angular radius RAYS_MESH_RADIUS·size/sizeRef, so the rays keep the
    // power and reach they had. The sky hides its own discs while rays are on (SkyDome).
    const angular = Math.min(Math.PI / 2, RAYS_MESH_RADIUS * size / sizeRef);
    this.raysMaskMat.uniforms.uCosEdge.value = Math.cos(angular);
    this.raysMaskMat.uniforms.uCosCore.value = Math.cos(angular * 0.98);
    this.raysRadialMat.uniforms.uLightUv.value.set(this.lightPos.x * 0.5 + 0.5, this.lightPos.y * 0.5 + 0.5);
    return true;
  }

  private applySettings(next: Partial<Settings>): void {
    const recreate = next.msaaSamples !== undefined && next.msaaSamples !== this.settings.msaaSamples;
    Object.assign(this.settings, next);
    const s = this.settings;
    this.ssaoMat.uniforms.uRadius.value = s.ssaoRadius;
    this.compositeMat.uniforms.uAoIntensity.value = s.ssaoIntensity;
    this.luminanceMat.uniforms.uThreshold.value = s.bloomThreshold;
    this.upMat.uniforms.uRadius.value = s.bloomRadius;
    this.compositeMat.uniforms.uBloomIntensity.value = s.bloomIntensity;
    this.raysRadialMat.uniforms.uDecay.value = s.godRaysDecay;
    this.raysRadialMat.uniforms.uExposure.value = s.godRaysExposure;
    this.compositeMat.uniforms.uGrade.value = Math.max(-1, Math.min(1, s.saturation - 1));
    if (recreate) { this.disposeTargets(); this.allocate(this.width, this.height); }
  }

  private allocate(width: number, height: number): void {
    this.width = width;
    this.height = height;
    const samples = Math.min(this.settings.msaaSamples, this.renderer.capabilities.maxSamples);
    const depthTexture = new THREE.DepthTexture(width, height, THREE.UnsignedIntType);
    this.scene = colorTarget(width, height, { srgb: true, depthBuffer: true, depthTexture, samples });
    const hw = Math.max(1, width >> 1), hh = Math.max(1, height >> 1);
    this.ao = colorTarget(hw, hh, { srgb: false, red: true });
    this.aoBlur = colorTarget(hw, hh, { srgb: false, red: true });
    this.ssaoMat.uniforms.uTexel.value.set(1 / hw, 1 / hh);
    this.aoBlurMat.uniforms.uTexel.value.set(1 / hw, 1 / hh);
    this.compositeMat.uniforms.uAoTexel.value.set(1 / hw, 1 / hh);
    this.bloom = [];
    this.bloomUp = [];
    for (let i = 0, w = hw, h = hh; i < BLOOM_LEVELS && w >= 2 && h >= 2; i++, w >>= 1, h >>= 1) {
      this.bloom.push(colorTarget(w, h, { srgb: true }));
      this.bloomUp.push(colorTarget(w, h, { srgb: true }));
    }
    this.raysA = colorTarget(hw, hh, { srgb: true });
    this.raysB = colorTarget(hw, hh, { srgb: true });
  }

  private disposeTargets(): void {
    this.scene?.depthTexture?.dispose();
    for (const t of [this.scene, this.ao, this.aoBlur, this.raysA, this.raysB, ...this.bloom, ...this.bloomUp]) t?.dispose();
    this.bloom = [];
    this.bloomUp = [];
  }
}

export function initPostPipeline(renderer: THREE.WebGLRenderer): PostPipeline {
  pipeline?.dispose();
  pipeline = new PostPipeline(renderer);
  return pipeline;
}

export function getPostPipeline(): PostPipeline | null {
  return pipeline;
}

export function disposePostPipeline(): void {
  pipeline?.dispose();
  pipeline = null;
}

/** God-ray march length — the quality preset's lever (20 on High, 60 on Ultra). */
export function setGodRaysSamples(samples: number): void {
  godRaysSamples = samples;
}
